<template>
    <v-dialog
        :value="isOpen"
        scrollable
        max-width="650px"
        transition="slide-y-transition"
        eager
        @keydown.esc="onClose"
        @click:outside="onClose"
    >
        <v-card>
            <v-card-title>
                <h1 class="text-subtitle-1">
                    <template v-if="isNewPackage">
                        Dodaj pakowanie
                    </template>
                    <template v-else>
                        Edycja pakowania
                    </template>
                </h1>
            </v-card-title>

            <div class="mx-5 my-1">
                <v-row>
                    <v-col cols="12" sm="5">
                        <v-select
                            v-model="packageModel.boxType"
                            :items="boxTypesData"
                            label="Sposób pakowania"
                            item-text="name"
                            return-object
                            outlined
                            dense
                            :error-messages="packageBoxTypeErrors"
                            @change="changeBoxType"
                        />
                    </v-col>
                    <v-col cols="12" sm="7">
                        <v-autocomplete
                            v-model="packageModel.packageType"
                            label="Opakowanie"
                            :items="availablePackages"
                            item-text="name"
                            return-object
                            outlined
                            dense
                            :error-messages="packagePackageTypeErrors"
                            @change="changePackageType"
                        />
                    </v-col>
                </v-row>
                <v-row>
                    <v-col cols="12" sm="4">
                        <v-text-field
                            v-model="packageModel.length"
                            label="Długość"
                            suffix="mm"
                            outlined
                            dense
                            :readonly="!isCustomMadePallet && !isCustomMadeCardBoard"
                            :error-messages="packageLengthErrors"
                        />
                    </v-col>
                    <v-col cols="12" sm="4">
                        <v-text-field
                            v-model="packageModel.width"
                            label="Szerokość"
                            suffix="mm"
                            outlined
                            dense
                            :readonly="!isCustomMadePallet && !isCustomMadeCardBoard"
                            :error-messages="packageWidthErrors"
                        />
                    </v-col>
                    <v-col cols="12" sm="4">
                        <v-text-field
                            v-model="packageModel.height"
                            label="Wysokość"
                            suffix="mm"
                            outlined
                            dense
                            :readonly="!isCustomMadeCardBoard"
                            :error-messages="packageHeightErrors"
                        />
                    </v-col>
                </v-row>
                <v-row>
                    <v-col cols="12" sm="6">
                        <v-text-field
                            v-model="formattedPackageWeight"
                            label="Waga palety/opakowania"
                            type="number"
                            step="0.005"
                            suffix="kg"
                            :min="0.005"
                            outlined
                            dense
                        />
                    </v-col>
                    <v-col cols="12" sm="6">
                        <v-text-field
                            v-model="packageModel.maxWeightCapacity"
                            label="Nośność opakowania"
                            type="number"
                            step="0.1"
                            suffix="kg"
                            :min="0.1"
                            outlined
                            dense
                        />
                    </v-col>
                </v-row>
                <v-row>
                    <v-col cols="12" sm="auto">
                        <v-checkbox
                            v-model="packageModel.setManuallyAmount"
                            class="mt-2"
                            label="Ręczna edycja ilości opakowań"
                            hide-details
                            outlined
                            dense
                        />
                    </v-col>
                    <v-col cols="12" sm="3">
                        <v-text-field
                            v-model="packageModel.amount"
                            label="Ilość opakowań"
                            type="number"
                            suffix="szt."
                            :min="0"
                            outlined
                            dense
                            :disabled="!setAmountManually"
                            :error-messages="packageAmountErrors"
                        />
                    </v-col>
                </v-row>
            </div>

            <v-divider />

            <v-card-actions>
                <v-btn
                    color="error"
                    text
                    data-sentry-label="Zamknij"
                    @click="onClose"
                >
                    Anuluj
                </v-btn>
                <v-spacer />
                <v-btn
                    color="success"
                    text
                    :data-sentry-label="isNewPackage ? 'Dodaj' : 'Zapisz'"
                    @click="onSave"
                >
                    <template v-if="isNewPackage">
                        Dodaj
                    </template>
                    <template v-else>
                        Zapisz
                    </template>
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import { isEmpty } from 'lodash'
import { required, integer, minValue } from 'vuelidate/lib/validators'

export default {
    name: 'PackageDialog',
    props: {
        isOpen: {
            type: Boolean
        },
        packageData: {
            type: Object,
            default: null
        },
        boxTypesData: {
            type: Array,
            default: null
        },
        packageTypesData: {
            type: Array,
            default: null
        },
    },
    data() {
        return {
            packageModel: {
                boxType: null,
                boxTypeId: null,
                packageType: null,
                packageTypeId: null,
                amount: 0,
                length: null,
                width: null,
                height: null,
                setManuallyAmount: false,
                maxWeightCapacity: 0.0,
                packageWeight: 0.0
            },
            filteredPackageTypes: [],
            availablePackages: [],
        }
    },
    validations() {
        const rules = {
            packageModel: {
                boxTypeId: {
                    required
                },
                packageTypeId: {
                    required
                },
                maxWeightCapacity: {
                    minValue: minValue(0.1)
                }
            }
        }

        if (this.isCustomMadePallet || this.isCustomMadeCardBoard) {
            rules.packageModel['length'] = {
                required,
                minValue: minValue(1)
            }

            rules.packageModel['width'] = {
                required,
                minValue: minValue(1)
            }
        }
        if (this.isCustomMadeCardBoard) {
            rules.packageModel['height'] = {
                required,
                minValue: minValue(1)
            }
        }

        if (this.setAmountManually) {
            rules.packageModel['amount'] = {
                required,
                minValue: minValue(1),
                integer
            }
        }

        rules.packageModel['maxWeightCapacity'] = {
            minValue: minValue(0.01),
        }

        return rules
    },
    computed: {
        formattedPackageWeight: {
            get() {
                return parseFloat(this.packageModel.packageWeight)
            },
            set(value) {
                this.packageModel.packageWeight = parseFloat(value)
            }
        },
        isNewPackage() {
            return isEmpty(this.packageData)
        },
        isCustomMadePallet() {
            return this.packageModel.packageTypeId === 34
        },
        isCustomMadeCardBoard() {
            return this.packageModel.packageTypeId === 46
        },
        setAmountManually() {
            return this.packageModel.setManuallyAmount
        },
        setMaxWeightCapacityManually() {
            return !this.setManuallyAmount
        },

        // Walidacje
        packageBoxTypeErrors() {
            const errors = []

            if (!this.$v.packageModel.boxTypeId.$dirty) return errors
            !this.$v.packageModel.boxTypeId.required && errors.push('Proszę podać sposób pakowania.')

            return errors
        },
        packagePackageTypeErrors() {
            const errors = []

            if (!this.$v.packageModel.packageTypeId.$dirty) return errors
            !this.$v.packageModel.packageTypeId.required && errors.push('Proszę podać rodzaj opakowania.')

            return errors
        },
        packageLengthErrors() {
            const errors = []

            if (!this.isCustomMadePallet && !this.isCustomMadeCardBoard) return errors

            if (!this.$v.packageModel.length.$dirty) return errors

            if (!this.$v.packageModel.length.required || !this.$v.packageModel.length.minValue) {
                errors.push('Proszę podać długość.')
            }

            return errors
        },
        packageWidthErrors() {
            const errors = []

            if (!this.isCustomMadePallet && !this.isCustomMadeCardBoard) return errors

            if (!this.$v.packageModel.width.$dirty) return errors

            if (!this.$v.packageModel.width.required || !this.$v.packageModel.width.minValue) {
                errors.push('Proszę podać szerokość.')
            }

            return errors
        },
        packageHeightErrors() {
            const errors = []

            if (!this.isCustomMadeCardBoard) return errors

            if (!this.$v.packageModel.height.$dirty) return errors

            if (!this.$v.packageModel.height.required || !this.$v.packageModel.height.minValue) {
                errors.push('Proszę podać wysokość.')
            }

            return errors
        },
        packageAmountErrors() {
            const errors = []

            if (!this.setAmountManually) return errors

            if (!this.$v.packageModel.amount.required) {
                errors.push('Liczba opakowań jest wymagana')
            }

            if (!this.$v.packageModel.amount.minValue || !this.$v.packageModel.amount.integer) {
                errors.push('Proszę podać całkowitą dodatnią liczbę opakowań')
            }

            return errors
        },
    },
    watch: {
        isOpen(boolean) {
            if (boolean) {
                if (!isEmpty(this.packageData)) {
                    this.packageModel = JSON.parse(JSON.stringify(this.packageData))
                }

                this.filteredPackageTypes = this.packageTypesData.filter(item => item.boxType.id === this.packageModel.boxTypeId)
                this.setAvailablePackages()
            } else {
                setTimeout(() => {
                    this.$v.$reset()
                    this.filteredPackageTypes = []
                    this.avaialblePackages = []
                    this.packageModel = {
                        boxType: null,
                        boxTypeId: null,
                        packageType: null,
                        packageTypeId: null,
                        amount: 0,
                        length: null,
                        width: null,
                        height: null,
                        maxWeightCapacity: 0,
                        packageWeight: 0
                    }
                }, 500)
            }
        }
    },
    methods: {
        setAvailablePackages() {
            // avaialble means all "visible" + selected if it's selected and invisible
            this.availablePackages = this.filteredPackageTypes.filter(item => item.isVisible === true)
            
            if (this.packageModel.packageType && this.packageModel.packageType.isVisible === false) {
                this.availablePackages.push(this.packageModel.packageType)
            }
        },
        changeBoxType(boxType) {
            this.packageModel.boxTypeId = boxType.id
            this.filteredPackageTypes = this.packageTypesData.filter(item => (item.boxType.id === boxType.id))
            this.setAvailablePackages()
            this.packageModel.packageType = null
            this.packageModel.packageTypeId = null
            this.packageModel.setManuallyAmount = false
            this.packageModel.setManuallyMaxWeightCapacity = false
            this.packageModel.maxWeightCapacity = 0.0
            this.packageModel.packageWeight = 0.0
        },
        changePackageType(packageType) {
            this.setAvailablePackages()
            this.packageModel.packageTypeId = packageType.id
            this.packageModel.length = packageType.length
            this.packageModel.width = packageType.width
            this.packageModel.height = packageType.height
            this.packageModel.maxWeightCapacity = packageType.maxWeightCapacity
            this.packageModel.setManuallyAmount = false
            this.packageModel.setManuallyMaxWeightCapacity = false
            this.packageModel.packageWeight = packageType.weight
        },
        onSave() {
            this.$v.$touch()

            if (this.$v.$invalid) {
                this.$store.dispatch('showNotification', {
                    type: 'error',
                    message: this.$i18n.t('validations.invalidMessage')
                })

                return
            }

            let payload = JSON.parse(JSON.stringify(this.packageModel))

            if (this.isNewPackage) {
                payload.shippingOrderPositionPackageWares = []
            }

            this.$emit('on-save', payload)
        },
        onClose() {
            this.$emit('on-close')
        }
    }
}
</script>